// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========

const isObject = (v: unknown): v is Record<string, unknown> =>
  v !== null && typeof v === 'object' && !Array.isArray(v);

/**
 * Deep-merge Hanggent-only strings under the upstream resources. Additive:
 * wherever base already has a value (or the types disagree) base wins, so an
 * upstream sync can never be overridden by the overlay. Returns a new object.
 */
export function mergeOverlay<T>(base: T, overlay: unknown): T {
  if (!isObject(base) || !isObject(overlay)) return base;
  const out: Record<string, unknown> = { ...base };
  for (const [key, value] of Object.entries(overlay)) {
    if (!(key in out)) {
      out[key] = structuredClone(value);
    } else if (isObject(out[key]) && isObject(value)) {
      out[key] = mergeOverlay(out[key], value);
    }
  }
  return out as T;
}
